/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module CportalModule */

import {
    ALBServicesConnectivityStatus,
    ALBServicesRegistrationStatus,
    IALBServicesStatus,
    ICluster,
} from 'generated-types';

import {
    configUpdateDuration,
    connectionPollingDuration,
    maxConnectionPollingCount,
} from 'ajs/modules/cportal/constants';

import {
    CportalService,
    IError,
    PortalInfo,
} from 'ajs/modules/cportal';

import {
    Component,
    Inject,
    OnDestroy,
    Type,
} from '@angular/core';

import {
    filter,
    first,
    take,
    takeUntil,
} from 'rxjs/operators';

import {
    AviContinueConfirmationComponent,
} from 'ng/modules/dialog/components/avi-continue-confirmation/avi-continue-confirmation.component';

import { ADMIN_TENANT_NAME } from 'ajs/modules/core/core.constants';
import { Auth } from 'ajs/modules/core/services';
import { DialogService } from 'ng/modules/core/services';
import { IPromise } from 'angular';
import { L10nService } from '@vmw/ngx-vip';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject } from 'rxjs';
import { waitFor } from 'ajs/js/utilities/timer';
import * as SystemInfoActions from 'ng/root-store/system-info/system-info.actions';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import './cloud-widget.component.less';
import * as globalL10n from 'global-l10n';
import { CPortalPageStore } from '../cportal-page.store';
import * as l10n from './cloud-widget.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Const ID for cloud service disconnection confirmation dialog.
 */
const DISCONNECT_FROM_CLOUD_SERVICE_WARNING_DIALOG_ID = 'DISCONNECT_FROM_CLOUD_SERVICE_WARNING_DIALOG_ID';

/**
 * ID for cancel registration confirmation dialog.
 */
const CANCEL_REGISTRATION_WARNING_DIALOG_ID = 'CANCEL_REGISTRATION_WARNING_DIALOG_ID';

type TPortalInfo = typeof PortalInfo;

/**
 * @description
 * Widget to display Cloud service within the cloud service page.
 * @author Deepak Chaudhary
 */
@Component({
    selector: 'cloud-widget',
    templateUrl: './cloud-widget.component.html',
})
export class CloudWidgetComponent implements OnDestroy {
    public readonly portalUrl$ = this.cPortalPageStore.portalUrl$;

    public readonly siteId$ = this.cPortalPageStore.siteId$;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * To indicate whether the tenant is an Admin tenant.
     */
    public isAdminTenant = true;

    /**
     * Name of existing controller.
     */
    public controllerName = '';

    /**
     * Default controller registration status.
     * Used to check if controller is Registered with the pulse.
     */
    public isControllerRegistered = false;

    /**
     * Default Avi cloud connected status.
     * Used to check if controller is connected with the pulse.
     */
    public aviCloudConnected = false;

    public deregisterLoading = false;

    /**
     * Instance of portalInfo Item.
     */
    private portalInfo: PortalInfo = null;

    /**
     * Subject used to unsubscribe observables.
     */
    private readonly ngDestroyed$ = new Subject();

    constructor(
        private readonly l10nService: L10nService,
        private readonly authService: Auth,
        private readonly cPortalPageStore: CPortalPageStore,
        private readonly cPortalService: CportalService,
        private readonly store: Store,
        @Inject('PortalInfo')
        private readonly PortalInfo: TPortalInfo,
        private readonly dialogService: DialogService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.isAdminTenant = this.authService.getTenantName() === ADMIN_TENANT_NAME;

        this.store.select(SystemInfoSelectors.selectLocalCluster)
            .pipe(take(1))
            .subscribe((cluster: ICluster) => this.controllerName = cluster?.name);

        this.cPortalPageStore
            .albServiceStatus$
            .pipe(takeUntil(this.ngDestroyed$))
            .subscribe((albServiceStatus: IALBServicesStatus) => {
                if (albServiceStatus) {
                    const {
                        registration_status: registrationStatus,
                        connectivity_status: connectivityStatus,
                    } = albServiceStatus;

                    this.isControllerRegistered =
                        registrationStatus === ALBServicesRegistrationStatus.ALBSERVICES_REGISTERED;
                    this.aviCloudConnected =
                        connectivityStatus === ALBServicesConnectivityStatus.ALBSERVICES_CONNECTED;
                }
            });
    }

    /**
     * Getter will return true if based on connectivity and registered status.
     */
    public get showFooterButtons(): boolean {
        return this.isUnregisteredAndDisconnected || this.isControllerRegistered || this.isUnregisteredAndConnected;
    }

    /**
     * If controller is unregistered and Avi cloud is disconnected getter will return true.
     */
    public get isUnregisteredAndDisconnected(): boolean {
        return !this.isControllerRegistered && !this.aviCloudConnected;
    }

    /**
     * If controller is unregistered and Avi cloud Connected getter will return true.
     */
    public get isUnregisteredAndConnected(): boolean {
        return !this.isControllerRegistered && this.aviCloudConnected;
    }

    public ngOnDestroy(): void {
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();

        if (this.portalInfo) {
            this.portalInfo.destroy();
        }
    }

    /**
     * This function authenticates user with the portal and then
     * it connects existing controller with Portal.
     */
    public connectAndRegisterController(): IPromise<void> {
        return this.connectAviCloud()
            .then(() => this.updateCtrlAndRegistrationDetails(connectionPollingDuration, maxConnectionPollingCount))
            .then(() => this.openRegistrationModal());
    }

    /**
     * Open dialog modal for registration with opt-in features.
     */
    public async openRegistrationModal(editMode = false): Promise<void> {
        const { l10nService } = this;

        if (!editMode) {
            if (!this.aviCloudConnected) {
                const errorMessage = l10nService.getMessage(l10nKeys.cloudServicesRegisterControllerMessage);

                return Promise.reject(new Error(errorMessage));
            }
        }

        this.portalInfo = new this.PortalInfo();
        await this.portalInfo.load();

        this.portalInfo.edit(null, {
            controllerName: this.controllerName,
            onComplete: () => this.updateCtrlAndRegistrationDetails(),
            reloadStore: () => this.cPortalPageStore.loadPortalInfo(),
            editMode,
        }).catch((error: IError) => Promise.reject(new Error(error.data as string ?? '')));
    }

    /**
     * Function to de-register Controller with Portal
     */
    public deregisterController(): void {
        this.dialogService.add({
            id: DISCONNECT_FROM_CLOUD_SERVICE_WARNING_DIALOG_ID,
            component: AviContinueConfirmationComponent as Type<Component>,
            componentProps: {
                customHeader: this.l10nService.getMessage(l10nKeys.disconnectFromCloudServiceLabel),
                warning: this.l10nService.getMessage(l10nKeys.disconnectFromCloudServiceMessage),
                onConfirm: () => {
                    this.deregisterLoading = true;
                    this.dialogService.remove(DISCONNECT_FROM_CLOUD_SERVICE_WARNING_DIALOG_ID);
                    this.cPortalService.deregisterController()
                        .then(() => waitFor(configUpdateDuration))
                        .then(() => this.updateStatus())
                        .catch((error: string) => this.cPortalPageStore.setError(error))
                        .finally(() => this.deregisterLoading = false);
                },
                onClose: () => {
                    this.dialogService.remove(DISCONNECT_FROM_CLOUD_SERVICE_WARNING_DIALOG_ID);
                },
            },
        });
    }

    /**
     * Function to cancel in-progress registration when controller is connected but not yet registered.
     * Clear the registration state and disconnects from Cloud Services.
     */
    public handleCancelRegistration(): void {
        this.dialogService.add({
            id: CANCEL_REGISTRATION_WARNING_DIALOG_ID,
            component: AviContinueConfirmationComponent as Type<Component>,
            componentProps: {
                customHeader: this.l10nService.getMessage(l10nKeys.cancelRegistrationLabel),
                warning: this.l10nService.getMessage(l10nKeys.cancelRegistrationMessage),
                onConfirm: () => {
                    this.deregisterLoading = true;
                    this.dialogService.remove(CANCEL_REGISTRATION_WARNING_DIALOG_ID);
                    this.cPortalPageStore.clearRegistration()
                        .then(() => waitFor(configUpdateDuration))
                        .then(() => this.updateStatus())
                        .catch((error: string) => this.cPortalPageStore.setError(error))
                        .finally(() => this.deregisterLoading = false);
                },
                onClose: () => {
                    this.dialogService.remove(CANCEL_REGISTRATION_WARNING_DIALOG_ID);
                },
            },
        });
    }

    /**
     * Function gets the latest registration status
     * and send the status to setStatus function.
     */
    private async updateStatus(): Promise<void> {
        await this.cPortalPageStore.loadConnectivityStatus();
    }

    /**
     * Update controller and registration details. If pollingTimeout and maxPollingCount
     * params are passed then Pulse connection status is polled after interval defined by
     * pollingTimeout for maximum number of times defined by maxPollingCount.
     * [pollingTimeout=configUpdateDuration] - timeout after which the status
     * will be polled.
     * [maxPollingCount=1] - maximum number of attempts for polling pulse
     * connection status.
     */
    private updateCtrlAndRegistrationDetails(
        pollingTimeout = configUpdateDuration,
        maxPollingCount = 1,
    ): IPromise<void> {
        return waitFor(pollingTimeout)
            .then(() => {
                this.store.dispatch(SystemInfoActions.passiveLoadSystemInfo());

                return firstValueFrom(
                    this.store.select(SystemInfoSelectors.selectLoaded)
                        .pipe(filter(Boolean), first()),
                );
            })
            .then(() => {
                this.store.select(SystemInfoSelectors.selectLocalCluster)
                    .pipe(take(1))
                    .subscribe((cluster: ICluster) => this.controllerName = cluster?.name);

                return this.updateStatus();
            })
            .then(() => {
                if (!this.aviCloudConnected && maxPollingCount > 1) {
                    return this.updateCtrlAndRegistrationDetails(pollingTimeout, maxPollingCount - 1);
                }
            });
    }

    /**
     * Function to authenticate user with Portal.
     */
    private connectAviCloud(): IPromise<void> {
        if (this.aviCloudConnected) {
            // If controller is already connected with portal then promise will be resolved and returned.
            return Promise.resolve();
        } else {
            return this.cPortalService.openLoginWindow();
        }
    }
}
